import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Target, Ban } from "lucide-react";
import { OddPill } from "./OddPill";
import { SG7Badge } from "./SG7Badge";
import { ExpandableReason } from "./ExpandableReason";
import type { TechnicalPick } from "../TechnicalPicksCard";

interface HeroPickCardProps {
  pick: TechnicalPick;
  displayOdds: number | null;
  isEstimated?: boolean;
  className?: string;
}

export function HeroPickCard({ 
  pick, 
  displayOdds,
  isEstimated = false,
  className 
}: HeroPickCardProps) {
  const { t } = useTranslation('analysis');
  const isNoBet = pick.noBet || pick.type === 'no_bet';

  return (
    <div
      className={cn(
        "relative p-5",
        "rounded-lg",
        "bg-[hsl(var(--sg7-surface-2))]",
        "border border-[hsl(var(--sg7-stroke-subtle))]",
        // SG7 Premium glow on hover/press
        "sg7-glow-hover",
        className
      )}
    >
      {/* Top accent stripe */}
      <div 
        className="absolute top-0 left-4 right-4 h-0.5 rounded-full"
        style={{
          background: isNoBet 
            ? 'hsl(var(--sg7-stripe-gray))' 
            : 'linear-gradient(90deg, hsl(var(--sg7-gold)), hsl(var(--sg7-gold-muted)))'
        }}
      />

      {/* Header row: Icon + Label + Odds */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {/* Icon */}
          <div className="flex-shrink-0 p-2 rounded-lg bg-[hsl(var(--sg7-surface-3))]">
            {isNoBet ? (
              <Ban className="h-5 w-5 text-[hsl(var(--sg7-text-muted))]" />
            ) : (
              <Target className="h-5 w-5 text-[hsl(var(--sg7-gold))]" />
            )}
          </div>

          {/* Label */}
          <div className="flex-1 min-w-0 pt-1">
            <h3 className={cn(
              "font-black text-base leading-tight",
              isNoBet 
                ? "text-[hsl(var(--sg7-text-muted))]" 
                : "text-[hsl(var(--sg7-text))]"
            )}>
              {pick.label}
            </h3>
          </div>
        </div>

        {/* Odds Pill or No Bet Badge */}
        {isNoBet ? (
          <span className="flex-shrink-0 px-3 py-1 rounded-md bg-[hsl(var(--sg7-surface-3))] text-[hsl(var(--sg7-text-muted))] text-xs font-bold">
            {t('picks.noBet')}
          </span>
        ) : (
          <OddPill odds={displayOdds} size="lg" variant="gold" isEstimated={isEstimated} />
        )}
      </div>

      {/* Badges row */}
      <div className="mt-3 flex flex-wrap gap-2">
        {pick.confidence && (
          <SG7Badge variant="confidence" level={pick.confidence} />
        )}
        {pick.isCombo && (
          <SG7Badge variant="combo" />
        )}
      </div>

      {/* Justification with expand/collapse */}
      {pick.justification && (
        <div className="mt-3">
          <ExpandableReason text={pick.justification} maxLines={2} />
        </div>
      )}
    </div>
  );
}